/**
 * Shared CSV employee import component.
 *
 * Used by both the broker (ClientEmployees) and employer (Employees) portals.
 * Encapsulates: CSV parsing, upload UI, preview, bulk import mutation,
 * org-items approval, and skipped-employees summary.
 *
 * Usage:
 *   <EmployeeImport clientId={11} onImportComplete={() => refetch()} />
 */

import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { downloadEmployeeImportTemplate } from "@/lib/exportUtils";
import { Download, FileUp, FileText, X, AlertTriangle } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EmployeeImportProps {
  clientId: number | string;
  /** Called after a successful import so the parent can refetch its employee list */
  onImportComplete?: () => void;
  /** Optional: query keys to invalidate on success */
  invalidateKeys?: unknown[][];
  /** Custom trigger button. Receives onClick to open the dialog. If omitted, renders a default "Import" button. */
  renderTrigger?: (onClick: () => void) => React.ReactNode;
}

interface PendingOrgItems {
  jobTitles: string[];
  departments: string[];
  locations: string[];
  employeeClasses: string[];
  divisions: string[];
}

interface SkippedEmployee {
  rowNumber: number;
  name: string;
  email?: string;
  missingFields: string[];
}

const EMPTY_ORG_ITEMS: PendingOrgItems = {
  jobTitles: [],
  departments: [],
  locations: [],
  employeeClasses: [],
  divisions: [],
};

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function stripQuotes(str: string): string {
  if (
    (str.startsWith('"') && str.endsWith('"')) ||
    (str.startsWith("'") && str.endsWith("'"))
  ) {
    return str.slice(1, -1);
  }
  return str;
}

function parseCSVText(text: string): any[] {
  const lines = text.split("\n").filter((l) => l.trim());
  if (lines.length === 0) return [];
  const headers = parseCSVLine(lines[0]).map((h) => stripQuotes(h.trim()));
  const records: any[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const record: any = {};
    headers.forEach((header, idx) => {
      const value = stripQuotes(values[idx] || "");
      record[header] = value;
      const lower = header.toLowerCase();
      const camel = lower.replace(/[_\s]+(.)?/g, (_, c) =>
        c ? c.toUpperCase() : ""
      );
      if (lower !== header) record[lower] = value;
      if (camel !== header && camel !== lower) record[camel] = value;
    });
    // Skip the template's sample row (tagged with "EXAMPLE" in employeeId)
    if (
      typeof record.employeeId === "string" &&
      record.employeeId.trim().toUpperCase().startsWith("EXAMPLE")
    ) {
      continue;
    }
    records.push(record);
  }
  return records;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmployeeImport({
  clientId,
  onImportComplete,
  invalidateKeys,
  renderTrigger,
}: EmployeeImportProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);

  const [isOrgApprovalOpen, setIsOrgApprovalOpen] = useState(false);
  const [pendingOrgItems, setPendingOrgItems] =
    useState<PendingOrgItems>(EMPTY_ORG_ITEMS);

  const [showAllNew, setShowAllNew] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [showAllUnchanged, setShowAllUnchanged] = useState(false);
  const [showAllPreview, setShowAllPreview] = useState(false);

  const [isMissingDataOpen, setIsMissingDataOpen] = useState(false);
  const [skippedEmployees, setSkippedEmployees] = useState<SkippedEmployee[]>(
    []
  );

  // --- Fetch existing employees for duplicate detection ---
  const { data: existingEmployees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees", { clientId }],
    queryFn: async () => {
      const res = await fetch(`/api/employees?clientId=${clientId}`, { credentials: "include" });
      if (!res.ok) return [];
      const json = await res.json();
      return Array.isArray(json) ? json : json.employees || [];
    },
    enabled: !!clientId,
    staleTime: 30_000, // refresh every 30s when dialog is used repeatedly
  });

  // --- Duplicate detection on parsed CSV data ---
  const duplicateInfo = useMemo(() => {
    const empty = { duplicates: [] as any[], withChanges: [] as any[], unchanged: [] as any[], newCount: csvData.filter(r => r.firstName && r.lastName).length, updateCount: 0, unchangedCount: 0 };
    if (csvData.length === 0 || existingEmployees.length === 0) return empty;

    const byEmail = new Map<string, any>();
    const byName = new Map<string, any>();
    existingEmployees.forEach((emp: any) => {
      if (emp.email) byEmail.set(emp.email.toLowerCase().trim(), emp);
      if (emp.firstName && emp.lastName) {
        byName.set(`${emp.firstName.toLowerCase().trim()}|${emp.lastName.toLowerCase().trim()}`, emp);
      }
    });

    const duplicates: Array<{ csvRow: any; existing: any; matchedBy: string; hasChanges: boolean; changedFields: Array<{ label: string; from: string; to: string }> }> = [];
    let newCount = 0;

    // Compare CSV row against existing employee and return changed fields
    const COMPARE_FIELDS: Array<{ key: string; label: string }> = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'department', label: 'Department' },
      { key: 'position', label: 'Position' },
      { key: 'salary', label: 'Salary' },
      { key: 'compensationAmount', label: 'Compensation' },
      { key: 'status', label: 'Status' },
      { key: 'address1', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zipCode', label: 'Zip Code' },
      { key: 'employmentType', label: 'Employment Type' },
      { key: 'scheduledHours', label: 'Hours' },
      { key: 'personalEmail', label: 'Personal Email' },
      { key: 'maritalStatus', label: 'Marital Status' },
      { key: 'tobaccoUser', label: 'Tobacco User' },
      { key: 'disabled', label: 'Disabled' },
    ];

    const getChangedFields = (csvRow: any, existing: any): Array<{ label: string; from: string; to: string }> => {
      const changes: Array<{ label: string; from: string; to: string }> = [];
      for (const { key, label } of COMPARE_FIELDS) {
        const csvVal = (csvRow[key] || "").toString().trim();
        const existVal = (existing[key] || "").toString().trim();
        if (csvVal && existVal && csvVal.toLowerCase() !== existVal.toLowerCase()) {
          changes.push({ label, from: existVal, to: csvVal });
        } else if (csvVal && !existVal) {
          changes.push({ label, from: "(empty)", to: csvVal });
        }
      }
      return changes;
    };

    for (const row of csvData) {
      if (!row.firstName || !row.lastName) continue;
      const email = row.email?.toLowerCase().trim();
      let matched: any = null;
      let matchedBy = "";

      if (email && byEmail.has(email)) {
        matched = byEmail.get(email);
        matchedBy = "email";
      } else if (!email) {
        const nameKey = `${row.firstName.toLowerCase().trim()}|${row.lastName.toLowerCase().trim()}`;
        if (byName.has(nameKey)) {
          matched = byName.get(nameKey);
          matchedBy = "name";
        }
      }

      if (matched) {
        const changedFields = getChangedFields(row, matched);
        duplicates.push({ csvRow: row, existing: matched, matchedBy, hasChanges: changedFields.length > 0, changedFields });
      } else {
        newCount++;
      }
    }

    const withChanges = duplicates.filter(d => d.hasChanges);
    const unchanged = duplicates.filter(d => !d.hasChanges);
    return { duplicates, withChanges, unchanged, newCount, updateCount: withChanges.length, unchangedCount: unchanged.length };
  }, [csvData, existingEmployees]);

  // --- File handling ---

  const resetState = () => {
    setCsvFile(null);
    setCsvData([]);
    setImportPreview([]);
    setShowAllNew(false);
    setShowAllUpdates(false);
    setShowAllUnchanged(false);
    const el = document.getElementById(
      "csv-upload-import"
    ) as HTMLInputElement | null;
    if (el) el.value = "";
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === "text/csv" || file.name.endsWith(".csv"))) {
      setCsvFile(file);
      setIsProcessingCsv(true);
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const records = parseCSVText(ev.target?.result as string);
          if (records.length === 0) {
            toast({ title: "CSV file is empty", variant: "destructive" });
          } else {
            setCsvData(records);
            // Store all records; the preview UI handles show-5 / show-all.
            setImportPreview(records);
            setShowAllPreview(false);
          }
        } catch (err) {
          toast({
            title: "Failed to parse CSV",
            description: (err as any).message,
            variant: "destructive",
          });
        }
        setIsProcessingCsv(false);
      };
      reader.readAsText(file);
    } else {
      toast({ title: "Please select a valid CSV file", variant: "destructive" });
    }
  };

  // --- Mutations ---

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("csvFile", file);
      formData.append("clientId", String(clientId));
      const res = await fetch("/api/employees/bulk-import", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        let message = `Failed to import (HTTP ${res.status})`;
        try {
          const err = await res.json();
          message = err.error || err.details || message;
        } catch {
          // Non-JSON response (proxy/gateway error) — keep the status-code message
        }
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      // Org items need approval?
      if (
        data.requiresApproval &&
        (data.missingJobTitles?.length ||
          data.missingDepartments?.length ||
          data.missingLocations?.length ||
          data.missingEmployeeClasses?.length ||
          data.missingDivisions?.length)
      ) {
        setPendingOrgItems({
          jobTitles: data.missingJobTitles || [],
          departments: data.missingDepartments || [],
          locations: data.missingLocations || [],
          employeeClasses: data.missingEmployeeClasses || [],
          divisions: data.missingDivisions || [],
        });
        setIsOrgApprovalOpen(true);
        return;
      }

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      if (invalidateKeys) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }

      const createdUsers = Number(data?.createdUsers ?? 0);
      toast({
        title: `Imported ${data.imported} employees`,
        description:
          createdUsers > 0
            ? `${data.created || 0} created, ${data.updated || 0} updated. ${createdUsers} new account${createdUsers === 1 ? "" : "s"} provisioned — send login emails from the Logins tab when ready.`
            : `${data.created || 0} created, ${data.updated || 0} updated.`,
        action:
          createdUsers > 0 && typeof clientId !== "undefined"
            ? (
                <ToastAction
                  altText="Open Logins tab"
                  onClick={() => {
                    window.location.href = `/clients/${clientId}?tab=employees&view=logins`;
                  }}
                >
                  Open Logins
                </ToastAction>
              )
            : undefined,
      });

      // Skipped?
      if (data.skippedEmployees?.length > 0) {
        setSkippedEmployees(data.skippedEmployees);
        setIsMissingDataOpen(true);
      }

      setIsOpen(false);
      resetState();
      onImportComplete?.();
    },
    onError: (error: any) => {
      toast({
        title: "Import failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createOrgItemsMutation = useMutation({
    mutationFn: async (items: PendingOrgItems) => {
      const res = await fetch("/api/employees/import/create-org-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId, ...items }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create org items");
      return res.json();
    },
    onSuccess: () => {
      setIsOrgApprovalOpen(false);
      setPendingOrgItems(EMPTY_ORG_ITEMS);
      if (csvFile) importMutation.mutate(csvFile);
    },
    onError: () =>
      toast({
        title: "Failed to create organization items",
        variant: "destructive",
      }),
  });

  const handleDownloadSkipped = () => {
    const headers = ["Row Number", "Name", "Email", "Missing Fields"];
    const rows = skippedEmployees.map((e) => [
      e.rowNumber,
      e.name,
      e.email || "N/A",
      e.missingFields.join(", "),
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        r
          .map((c) => (String(c).includes(",") ? `"${c}"` : c))
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `skipped_employees_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // --- Derived ---

  const validCount = csvData.filter((r) => r.firstName && r.lastName).length;
  const skippedCount = csvData.length - validCount;
  const hasMissingEmails = csvData.some(
    (r) => r.firstName && r.lastName && !r.email
  );

  // --- Render ---

  return (
    <>
      {/* Trigger button */}
      {renderTrigger ? (
        renderTrigger(() => setIsOpen(true))
      ) : (
        <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
          <FileUp className="h-4 w-4 mr-2" />
          Import
        </Button>
      )}

      {/* Import dialog */}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) resetState();
        }}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Import Employees from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with employee data. Download the template to see
              the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 flex-1 overflow-y-auto pr-1">
            <Button
              variant="outline"
              onClick={() => downloadEmployeeImportTemplate()}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" /> Download Import Template
            </Button>

            <p className="text-xs text-muted-foreground">
              Dates can be{" "}
              <span className="font-mono font-medium">YYYY-MM-DD</span> (e.g.{" "}
              <span className="font-mono font-medium">1985-03-15</span>) or{" "}
              <span className="font-mono font-medium">M/D/YYYY</span> (e.g.{" "}
              <span className="font-mono font-medium">3/15/1985</span>), so files
              edited in Excel import fine — prefer 4-digit years. See the sample
              row in the template for how to fill in each field.
            </p>

            <div className="space-y-4 border-t pt-4">
              {!csvFile && (
                <label
                  htmlFor="csv-upload-import"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-muted-foreground/25 hover:border-primary bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <FileUp className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Click to upload</span> your
                    CSV file
                  </p>
                  <input
                    id="csv-upload-import"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </label>
              )}
              {csvFile && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800 relative">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({csvData.length} rows)
                  </span>
                  <button
                    className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground hover:text-destructive p-1"
                    onClick={resetState}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {isProcessingCsv && (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <span className="ml-2">Processing CSV...</span>
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="space-y-4">
                {(() => {
                  const PREVIEW_LIMIT = 5;
                  const visible = showAllPreview
                    ? importPreview
                    : importPreview.slice(0, PREVIEW_LIMIT);
                  const hidden = importPreview.length - visible.length;
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">
                          Preview ({visible.length} of {importPreview.length}{" "}
                          row{importPreview.length === 1 ? "" : "s"})
                        </h4>
                        {importPreview.length > PREVIEW_LIMIT && (
                          <button
                            type="button"
                            onClick={() => setShowAllPreview((v) => !v)}
                            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                          >
                            {showAllPreview
                              ? "Show first 5"
                              : `Show all ${importPreview.length}`}
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto border rounded-lg dark:border-gray-700 max-h-[480px]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              {[
                                "First Name",
                                "Last Name",
                                "Email",
                                "Department",
                                "Position",
                                "Hire Date",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {visible.map((row, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2 text-sm">
                                  {row.firstName || "-"}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {row.lastName || "-"}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {row.email || "-"}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {row.department || "-"}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {row.position || "-"}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {row.hireDate || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {!showAllPreview && hidden > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {hidden} more row{hidden === 1 ? "" : "s"} hidden —
                          click "Show all" to see them.
                        </p>
                      )}
                    </>
                  );
                })()}
                <div className="text-sm space-y-2">
                  {/* Import summary with employee names */}
                  <div className="space-y-3">
                    {/* New employees */}
                    {duplicateInfo.newCount > 0 && (() => {
                      const newRows = csvData.filter(r => {
                        if (!r.firstName || !r.lastName) return false;
                        return !duplicateInfo.duplicates.some(d =>
                          d.csvRow.firstName === r.firstName && d.csvRow.lastName === r.lastName && d.csvRow.email === r.email
                        );
                      });
                      const LIMIT = 5;
                      const visible = showAllNew ? newRows : newRows.slice(0, LIMIT);
                      const hasMore = newRows.length > LIMIT;
                      return (
                        <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 space-y-2">
                          <div className="flex items-center gap-2 text-green-900 dark:text-green-100">
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                            <span className="text-sm font-medium">{duplicateInfo.newCount} new employee{duplicateInfo.newCount !== 1 ? "s" : ""} will be added</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {visible.map((r: any, i: number) => (
                              <span key={i} className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded">
                                {r.firstName} {r.lastName}
                              </span>
                            ))}
                          </div>
                          {hasMore && (
                            <button className="text-xs text-green-700 dark:text-green-300 hover:underline" onClick={() => setShowAllNew(!showAllNew)}>
                              {showAllNew ? "Show less" : `Show all ${newRows.length}`}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Existing employees with changes */}
                    {duplicateInfo.withChanges.length > 0 && (() => {
                      const LIMIT = 5;
                      const visible = showAllUpdates ? duplicateInfo.withChanges : duplicateInfo.withChanges.slice(0, LIMIT);
                      const hasMore = duplicateInfo.withChanges.length > LIMIT;
                      return (
                        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-3 space-y-2">
                          <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm font-medium">{duplicateInfo.withChanges.length} existing employee{duplicateInfo.withChanges.length !== 1 ? "s" : ""} will be updated</span>
                          </div>
                          <div className="space-y-2">
                            {visible.map((d: any, i: number) => (
                              <div key={i} className="text-xs space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-amber-800 dark:text-amber-200 font-medium">{d.existing.firstName} {d.existing.lastName}</span>
                                  <span className="text-amber-600 dark:text-amber-400">matched by {d.matchedBy}</span>
                                </div>
                                <div className="ml-3 space-y-0.5">
                                  {d.changedFields.map((cf: any, j: number) => (
                                    <div key={j} className="text-amber-700 dark:text-amber-300">
                                      <span className="text-amber-500">{cf.label}:</span>{" "}
                                      <span className="line-through opacity-60">{cf.from}</span>{" → "}
                                      <span className="font-medium">{cf.to}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          {hasMore && (
                            <button className="text-xs text-amber-700 dark:text-amber-300 hover:underline" onClick={() => setShowAllUpdates(!showAllUpdates)}>
                              {showAllUpdates ? "Show less" : `Show all ${duplicateInfo.withChanges.length}`}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Already in system — no changes */}
                    {duplicateInfo.unchangedCount > 0 && (() => {
                      const LIMIT = 5;
                      const visible = showAllUnchanged ? duplicateInfo.unchanged : duplicateInfo.unchanged.slice(0, LIMIT);
                      const hasMore = duplicateInfo.unchanged.length > LIMIT;
                      return (
                        <div className="rounded-lg border p-3 space-y-2">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span className="inline-block w-2 h-2 bg-gray-400 rounded-full" />
                            <span className="text-sm">{duplicateInfo.unchangedCount} already in the system — will be skipped</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {visible.map((d: any, i: number) => (
                              <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                                {d.existing.firstName} {d.existing.lastName}
                              </span>
                            ))}
                          </div>
                          {hasMore && (
                            <button className="text-xs text-muted-foreground hover:underline" onClick={() => setShowAllUnchanged(!showAllUnchanged)}>
                              {showAllUnchanged ? "Show less" : `Show all ${duplicateInfo.unchanged.length}`}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Skipped rows */}
                    {skippedCount > 0 && (
                      <div className="text-xs text-yellow-600 dark:text-yellow-400">
                        {skippedCount} row{skippedCount !== 1 ? "s" : ""} will be skipped (missing first or last name)
                      </div>
                    )}
                  </div>

                  {hasMissingEmails && (
                    <p className="text-blue-600 dark:text-blue-400 text-xs">
                      Some employees missing email — they'll be imported without login credentials.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                resetState();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => csvFile && importMutation.mutate(csvFile)}
              disabled={!csvFile || importMutation.isPending}
            >
              {importMutation.isPending ? "Importing..." : "Import Employees"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Org Items Approval Dialog */}
      <Dialog open={isOrgApprovalOpen} onOpenChange={setIsOrgApprovalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Missing Organization Items</DialogTitle>
            <DialogDescription>
              The following items need to be created before importing employees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(
              [
                { items: pendingOrgItems.jobTitles, label: "Job Titles", color: "bg-blue-500" },
                { items: pendingOrgItems.departments, label: "Departments", color: "bg-green-500" },
                { items: pendingOrgItems.locations, label: "Locations", color: "bg-purple-500" },
                { items: pendingOrgItems.employeeClasses, label: "Employee Classes", color: "bg-orange-500" },
                { items: pendingOrgItems.divisions, label: "Divisions", color: "bg-red-500" },
              ] as const
            )
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <div key={g.label}>
                  <h4 className="font-medium mb-2 text-sm">
                    {g.label} to Create
                  </h4>
                  <ul className="space-y-1 bg-muted/50 p-3 rounded-lg border">
                    {g.items.map((item) => (
                      <li key={item} className="text-sm flex items-center">
                        <span
                          className={`inline-block w-2 h-2 ${g.color} rounded-full mr-2`}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
              <AlertDescription className="text-blue-900 dark:text-blue-100">
                These items will be created in your organization, then the import
                will continue automatically.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOrgApprovalOpen(false);
                setPendingOrgItems(EMPTY_ORG_ITEMS);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createOrgItemsMutation.mutate(pendingOrgItems)}
              disabled={createOrgItemsMutation.isPending}
            >
              {createOrgItemsMutation.isPending
                ? "Creating..."
                : "Create Items & Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skipped Employees Dialog */}
      <Dialog open={isMissingDataOpen} onOpenChange={setIsMissingDataOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              {skippedEmployees.length} Employee
              {skippedEmployees.length !== 1 ? "s" : ""} Skipped
            </DialogTitle>
            <DialogDescription>
              These rows were skipped because they were missing required fields.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {skippedEmployees.map((emp, i) => (
              <div
                key={i}
                className="p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Row {emp.rowNumber}: {emp.name}
                  </span>
                  {emp.email && (
                    <span className="text-xs text-muted-foreground">
                      {emp.email}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Missing: {emp.missingFields.join(", ")}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleDownloadSkipped}>
              Download Skipped List
            </Button>
            <Button onClick={() => setIsMissingDataOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
